import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Mail, Phone, Calendar, FileText, MessageSquare, Clock, Send, Trash2,
  CalendarCheck, Users, MapPin, Edit, Plus, UserCheck, ClipboardList, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, HelpCircle, ExternalLink, ChevronDown, Check, X,
} from 'lucide-react';
import { API_URL } from '../config/api';
import BlueHeader from '../components/BlueHeader';
import { useFiliale } from '../context/FilialeContext';
import http from '../api/http';

/* ════════════════════════════════════════════════════════════════════
   Utilitaires et éléments de présentation (aucune action métier ici)
   ════════════════════════════════════════════════════════════════════ */

const cx = (...c) => c.filter(Boolean).join(' ');

const fieldId = (label) =>
  'f-' + String(label).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const controlCls =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 read-only:bg-slate-50 read-only:text-slate-600';

const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

const btnSecondary =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40';

const ACTION_TONES = {
  blue: {
    solid: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-600/40',
    outline: 'border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 focus-visible:ring-blue-600/40',
  },
  emerald: {
    solid: 'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-600/40',
    outline: 'border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 focus-visible:ring-emerald-600/40',
  },
  rose: {
    solid: 'bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-600/40',
    outline: 'border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 focus-visible:ring-rose-600/40',
  },
  amber: {
    solid: 'bg-amber-600 text-white hover:bg-amber-700 focus-visible:ring-amber-600/40',
    outline: 'border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 focus-visible:ring-amber-600/40',
  },
  slate: {
    solid: 'bg-slate-800 text-white hover:bg-slate-900 focus-visible:ring-slate-600/40',
    outline: 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 focus-visible:ring-slate-600/40',
  },
};

const ActionButton = ({ tone = 'blue', variant = 'solid', icon: Icon, children, ...props }) => (
  <button
    type="button"
    className={cx(
      'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      ACTION_TONES[tone][variant],
    )}
    {...props}
  >
    {Icon && <Icon className="h-4 w-4" />}
    {children}
  </button>
);

const STATUT_BADGES = {
  recue: 'bg-slate-100 text-slate-700 ring-slate-500/20',
  test: 'bg-purple-50 text-purple-700 ring-purple-600/20',
  en_examen: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  entretien: 'bg-sky-50 text-sky-700 ring-sky-600/20',
  offre: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  embauchee: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  refusee: 'bg-rose-50 text-rose-700 ring-rose-600/20',
  retiree: 'bg-gray-100 text-gray-600 ring-gray-500/20',
};
const STATUT_LABELS = {
  recue: 'Reçue', test: 'Test', en_examen: 'En examen', entretien: 'Entretien',
  offre: 'Offre', embauchee: 'Embauchée', refusee: 'Refusée', retiree: 'Retirée',
};
const statutBadge = (statut) => (
  <span className={cx('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset', STATUT_BADGES[statut] || STATUT_BADGES.recue)}>
    {STATUT_LABELS[statut] || statut}
  </span>
);

const TEST_STATUT = {
  termine: { label: 'Terminé', cls: 'text-emerald-700' },
  expire: { label: 'Expiré', cls: 'text-amber-700' },
};
const testStatut = (s) => TEST_STATUT[s] || { label: s, cls: 'text-slate-600' };

const Heading = ({ children, description }) => (
  <div>
    <h3 className="text-sm font-semibold text-slate-900">{children}</h3>
    {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
  </div>
);

const Stat = ({ label, children, className }) => (
  <div className="min-w-0">
    <dt className="text-xs text-slate-500">{label}</dt>
    <dd className={cx('mt-1 font-semibold text-slate-900', className)}>{children}</dd>
  </div>
);

const EmptyState = ({ children }) => (
  <div className="rounded-xl border border-dashed border-slate-300 py-12 text-center text-sm text-slate-500">{children}</div>
);

const Field = ({ label, required, hint, className = '', htmlFor, children }) => (
  <div className={className}>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-slate-700">
      {label}
      {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
    </label>
    {children}
    {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
  </div>
);

const TextField = ({ label, hint, className, ...props }) => {
  const id = fieldId(label);
  return (
    <Field label={label} required={props.required} hint={hint} htmlFor={id} className={className}>
      <input id={id} className={controlCls} {...props} />
    </Field>
  );
};

const TextArea = ({ label, hint, className, ...props }) => {
  const id = fieldId(label);
  return (
    <Field label={label} required={props.required} hint={hint} htmlFor={id} className={className}>
      <textarea id={id} className={controlCls} {...props} />
    </Field>
  );
};

const SelectField = ({ label, hint, className, children, ...props }) => {
  const id = fieldId(label);
  return (
    <Field label={label} required={props.required} hint={hint} htmlFor={id} className={className}>
      <div className="relative">
        <select id={id} className={cx(controlCls, 'appearance-none pr-9')} {...props}>{children}</select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
    </Field>
  );
};

const Modal = ({ title, onClose, footer, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-black/10"
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">{children}</div>
      <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">{footer}</div>
    </div>
  </div>
);

/* ───────── Avancement de la candidature ───────── */

const PIPELINE = [
  { id: 'recue', label: 'Reçue' },
  { id: 'test', label: 'Test' },
  { id: 'en_examen', label: 'En examen' },
  { id: 'entretien', label: 'Entretien' },
  { id: 'offre', label: 'Offre' },
  { id: 'embauchee', label: 'Embauchée' },
];

const Pipeline = ({ current }) => (
  <ol className="flex w-full items-start" aria-label="Avancement de la candidature">
    {PIPELINE.map((s, i) => {
      const isCurrent = i === current;
      const done = i < current || (isCurrent && s.id === 'embauchee');
      return (
        <li key={s.id} className="relative flex-1" aria-current={isCurrent ? 'step' : undefined}>
          {i > 0 && (
            <span
              aria-hidden="true"
              className={cx('absolute right-1/2 top-3.5 h-0.5 w-full', i <= current ? 'bg-blue-600' : 'bg-slate-200')}
            />
          )}
          <span
            className={cx(
              'relative z-10 mx-auto flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold',
              done
                ? 'bg-blue-600 text-white'
                : isCurrent
                  ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                  : 'bg-white text-slate-400 ring-1 ring-inset ring-slate-300',
            )}
          >
            {done ? <Check className="h-4 w-4" /> : i + 1}
          </span>
          <span className={cx('mt-2 block text-center text-xs leading-tight', isCurrent ? 'font-semibold text-slate-900' : done ? 'text-slate-700' : 'text-slate-400')}>
            {s.label}
          </span>
        </li>
      );
    })}
  </ol>
);

/* ───────── Triage semi-automatique ───────── */

const toNum = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
const norm = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const splitSkills = (s) => String(s || '').split(',').map((c) => c.trim()).filter(Boolean);
const mentionsSkill = (text, skill) => new RegExp(`(^|[^a-z0-9])${escapeRe(norm(skill))}([^a-z0-9]|$)`).test(text);

// Calcule le verdict à partir des données déjà chargées (aucun appel réseau)
const buildTriage = (candidature, analyse) => {
  const session = analyse?.session;
  const score = toNum(session ? session.score_obtenu : candidature.score_test);
  const seuil = toNum(session?.note_passage) ?? 60;
  const scoreOk = score === null ? null : score >= seuil;

  const expMin = toNum(analyse?.offre?.experience_min);
  const expMax = toNum(analyse?.offre?.experience_max);
  const expVal = analyse?.candidat ? (toNum(analyse.candidat.experience_annees) ?? 0) : null;
  let expState = 'na';
  if (expVal !== null && expMin !== null && expMax !== null) {
    expState = expVal < expMin ? 'below' : expVal > expMax ? 'above' : 'ok';
  }

  const skills = splitSkills(analyse?.candidat?.competences ?? candidature.competences);
  const profil = norm(analyse?.offre?.profil_recherche);
  const matched = profil ? skills.filter((s) => mentionsSkill(profil, s)) : [];

  let verdict = 'indetermine';
  if (scoreOk === false) verdict = 'non_recommande';
  else if (scoreOk === true) verdict = expState === 'below' || expState === 'above' ? 'a_examiner' : 'recommande';

  return { score, seuil, scoreOk, expMin, expMax, expVal, expState, skills, matched, hasProfil: Boolean(profil), verdict };
};

const VERDICTS = {
  recommande: { title: 'Recommandé pour examen', Icon: CheckCircle2, soft: 'bg-emerald-50/70', text: 'text-emerald-700', ring: 'text-emerald-500' },
  a_examiner: { title: 'À examiner manuellement', Icon: AlertTriangle, soft: 'bg-amber-50/70', text: 'text-amber-700', ring: 'text-amber-500' },
  non_recommande: { title: 'Non recommandé', Icon: XCircle, soft: 'bg-rose-50/70', text: 'text-rose-700', ring: 'text-rose-500' },
  indetermine: { title: 'Triage indisponible', Icon: HelpCircle, soft: 'bg-slate-50', text: 'text-slate-600', ring: 'text-slate-300' },
};

const triageReason = (t) => {
  switch (t.verdict) {
    case 'recommande':
      return t.expState === 'ok'
        ? 'Le test est réussi et l’expérience du candidat correspond à la fourchette demandée.'
        : 'Le test est réussi. L’expérience n’a pas pu être comparée à celle demandée dans l’offre.';
    case 'a_examiner':
      return t.expState === 'below'
        ? 'Le test est réussi, mais l’expérience du candidat est inférieure au minimum demandé. Une vérification manuelle est conseillée.'
        : 'Le test est réussi, mais l’expérience du candidat dépasse le maximum demandé. Une vérification manuelle est conseillée.';
    case 'non_recommande':
      return `Le score est inférieur au seuil de réussite de ${t.seuil}%. Une vérification manuelle reste possible.`;
    default:
      return 'Aucun score n’est disponible pour ce candidat.';
  }
};

const ScoreRing = ({ score, seuil, toneClass }) => {
  const size = 136;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = score === null ? 0 : Math.max(0, Math.min(100, score));
  const mid = size / 2;
  const ang = ((Math.max(0, Math.min(100, seuil)) / 100) * 360 - 90) * (Math.PI / 180);
  const x1 = mid + (r - stroke / 2 - 2) * Math.cos(ang);
  const y1 = mid + (r - stroke / 2 - 2) * Math.sin(ang);
  const x2 = mid + (r + stroke / 2 + 2) * Math.cos(ang);
  const y2 = mid + (r + stroke / 2 + 2) * Math.sin(ang);
  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={score === null ? 'Score indisponible' : `Score de ${score}% pour un seuil de ${seuil}%`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={mid} cy={mid} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-white" />
        <circle
          cx={mid} cy={mid} r={r} fill="none" stroke="currentColor" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${(v / 100) * c} ${c}`}
          transform={`rotate(-90 ${mid} ${mid})`} className={toneClass}
        />
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-slate-900" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-semibold tabular-nums text-slate-900">{score === null ? '—' : `${score}%`}</span>
        <span className="text-xs text-slate-500">seuil {seuil}%</span>
      </div>
    </div>
  );
};

const BarGauge = ({ value, marker, ok, tone = 'score' }) => {
  const v = Math.max(0, Math.min(100, value ?? 0));
  const m = marker === null || marker === undefined ? null : Math.max(0, Math.min(100, marker));
  const barCls = tone === 'neutral' ? 'bg-blue-500' : ok === false ? 'bg-rose-500' : 'bg-emerald-500';
  return (
    <div className="relative pt-5">
      {m !== null && (
        <>
          <span
            className={cx('absolute top-0 whitespace-nowrap text-[11px] font-medium text-slate-500', m > 85 ? '-translate-x-full' : m < 15 ? '' : '-translate-x-1/2')}
            style={{ left: `${m}%` }}
          >
            Seuil {m}%
          </span>
          <span className="absolute top-[17px] h-4 w-0.5 -translate-x-1/2 rounded bg-slate-900" style={{ left: `${m}%` }} />
        </>
      )}
      <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
        <div className={cx('h-full rounded-full transition-all duration-500 motion-reduce:transition-none', barCls)} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
};

const RangeGauge = ({ min, max, value, state }) => {
  const domain = Math.max(max * 1.5, (value ?? 0) * 1.15, min + 1, 1);
  const pct = (n) => `${Math.max(0, Math.min(100, (n / domain) * 100))}%`;
  return (
    <div className="relative h-12">
      <div className="absolute inset-x-0 top-3 h-2.5 rounded-full bg-slate-100" />
      <div className="absolute top-3 h-2.5 rounded-full bg-blue-200" style={{ left: pct(min), width: `calc(${pct(max)} - ${pct(min)})` }} />
      {value !== null && (
        <span
          className={cx('absolute top-[7px] h-[18px] w-1.5 -translate-x-1/2 rounded-full ring-2 ring-white', state === 'ok' ? 'bg-emerald-600' : 'bg-amber-500')}
          style={{ left: pct(value) }}
        />
      )}
      <span className="absolute top-[30px] -translate-x-1/2 text-[11px] text-slate-500" style={{ left: pct(min) }}>{min}</span>
      <span className="absolute top-[30px] -translate-x-1/2 text-[11px] text-slate-500" style={{ left: pct(max) }}>{max}</span>
    </div>
  );
};

const STATE_ICON = {
  ok: { Icon: CheckCircle2, cls: 'text-emerald-600' },
  warn: { Icon: AlertTriangle, cls: 'text-amber-500' },
  fail: { Icon: XCircle, cls: 'text-rose-500' },
  na: { Icon: HelpCircle, cls: 'text-slate-300' },
};

const CriterionRow = ({ state, title, requirement, gauge, value, note }) => {
  const { Icon, cls } = STATE_ICON[state];
  return (
    <div className="grid items-center gap-x-6 gap-y-3 py-5 md:grid-cols-[240px_minmax(0,1fr)_130px]">
      <div className="flex items-start gap-3">
        <Icon className={cx('mt-0.5 h-5 w-5 shrink-0', cls)} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="text-sm text-slate-500">{requirement}</p>
        </div>
      </div>
      <div className="min-w-0">{gauge}</div>
      <div className="md:text-right">
        <p className="text-lg font-semibold tabular-nums text-slate-900">{value}</p>
        {note && <p className="text-xs text-slate-500">{note}</p>}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   Page
   ════════════════════════════════════════════════════════════════════ */

const CandidatureDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { filialeId } = useFiliale();
  const [candidature, setCandidature] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [entretiens, setEntretiens] = useState([]);
  const [evaluateurs, setEvaluateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profil');
  const [commentaire, setCommentaire] = useState('');
  const [showEntretienModal, setShowEntretienModal] = useState(false);
  const [selectedEntretien, setSelectedEntretien] = useState(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testQuestionnaires, setTestQuestionnaires] = useState([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState('');
  const [testDateLimite, setTestDateLimite] = useState('');
  const [testAnalyse, setTestAnalyse] = useState(null);
  const [loadingAnalyse, setLoadingAnalyse] = useState(false);
  const [evaluateurSearch, setEvaluateurSearch] = useState('');
  const [entretienForm, setEntretienForm] = useState({
    date_heure_entretien: '',
    lieu_entretien: '',
    type_entretien: 'Physique',
    evaluateurs_ids: []
  });
  const [onboardingData, setOnboardingData] = useState({
    date_embauche: '',
    type_contrat: 'CDI',
    commentaire: ''
  });

  const tabs = [
    { id: 'profil', label: 'Profil', icon: User },
    { id: 'test', label: 'Test', icon: ClipboardList },
    { id: 'entretiens', label: 'Entretiens', icon: CalendarCheck },
    { id: 'historique', label: 'Historique', icon: Clock },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const [candResp, histResp, entResp, evalResp] = await Promise.all([
        http.get(`/api/candidatures/${id}`),
        http.get(`/api/historique-candidature/candidature/${id}`),
        http.get(`/api/entretiens/candidature/${id}`),
        http.get('/api/entretiens/evaluateurs', { params: filialeId && filialeId !== 'all' ? { filiale_id: filialeId } : {} })
      ]);
      setCandidature(candResp.data);
      setHistorique(Array.isArray(histResp.data) ? histResp.data : []);
      setEntretiens(Array.isArray(entResp.data) ? entResp.data : []);
      setEvaluateurs(Array.isArray(evalResp.data) ? evalResp.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur chargement candidature');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (candidature) {
      fetchQuestionnaires();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidature]);

  const handleAddCommentaire = async () => {
    if (!commentaire.trim()) return;
    try {
      await http.post('/api/historique-candidature/commentaire', {
        candidature_id: id,
        commentaire
      });
      setCommentaire('');
      fetchData();
    } catch (error) {
      alert('Erreur ajout commentaire');
    }
  };

  const handlePlanifierEntretien = async () => {
    try {
      await http.post('/api/entretiens/planifier', {
        candidature_id: id,
        ...entretienForm,
        evaluateurs_ids: entretienForm.evaluateurs_ids.join(',')
      });
      setShowEntretienModal(false);
      setEntretienForm({
        date_heure_entretien: '',
        lieu_entretien: '',
        type_entretien: 'Physique',
        evaluateurs_ids: []
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur planification');
    }
  };

  const handleModifierEntretien = async (entretien) => {
    try {
      await http.put(`/api/entretiens/${entretien.id}`, {
        ...entretienForm,
        evaluateurs_ids: entretienForm.evaluateurs_ids.join(',')
      });
      setShowEntretienModal(false);
      setEntretienForm({
        date_heure_entretien: '',
        lieu_entretien: '',
        type_entretien: 'Physique',
        evaluateurs_ids: []
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur modification');
    }
  };

  const handleSupprimerEntretien = async (entretienId) => {
    if (!window.confirm('Supprimer cet entretien ?')) return;
    try {
      await http.delete(`/api/entretiens/${entretienId}`);
      fetchData();
    } catch (error) {
      alert('Erreur suppression');
    }
  };

  const openEntretienModal = (entretien = null) => {
    setSelectedEntretien(entretien);
    if (entretien) {
      setEntretienForm({
        date_heure_entretien: entretien.date_heure_entretien ? entretien.date_heure_entretien.slice(0, 16) : '',
        lieu_entretien: entretien.lieu_entretien || '',
        type_entretien: entretien.type_entretien || 'Physique',
        evaluateurs_ids: entretien.evaluateurs_ids || []
      });
    } else {
      setEntretienForm({
        date_heure_entretien: '',
        lieu_entretien: '',
        type_entretien: 'Physique',
        evaluateurs_ids: []
      });
    }
    setShowEntretienModal(true);
  };

  const handleOnboarding = async () => {
    try {
      const response = await http.patch(`/api/candidatures/${id}/statut`, {
        statut: 'embauchee',
        ...onboardingData
      });

      setShowOnboardingModal(false);
      setOnboardingData({ date_embauche: '', type_contrat: 'CDI', commentaire: '' });
      fetchData();

      if (response.data.onboarding) {
        alert(`Candidature transformée en employé ! ID employé: ${response.data.onboarding.personnelId}`);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur onboarding');
    }
  };

  const handlePlanifierTest = async () => {
    try {
      const payload = {
        candidature_id: id,
        date_limite: testDateLimite || null
      };
      // S'il y a plusieurs questionnaires, permettre la sélection
      if (testQuestionnaires.length > 1) {
        payload.questionnaire_id = selectedQuestionnaire || null;
      }
      await http.post('/api/questionnaire-responses/planifier', payload);
      setShowTestModal(false);
      setSelectedQuestionnaire('');
      setTestDateLimite('');
      fetchData();
      alert('Test planifié avec succès. Le candidat a été notifié.');
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur planification test');
    }
  };

  const handleResetTest = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser le test ? Le candidat devra repasser le test.')) return;
    try {
      await http.delete(`/api/questionnaire-responses/candidature/${id}`);
      fetchData();
      alert('Test réinitialisé avec succès. Le statut est revenu à "Reçue".');
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur réinitialisation test');
    }
  };

  const handlePasserEnExamen = async () => {
    if (!window.confirm('Passer cette candidature en examen ?')) return;
    try {
      await http.patch(`/api/candidatures/${id}/statut`, { statut: 'en_examen' });
      fetchData();
      alert('Candidature passée en examen avec succès');
    } catch (error) {
      alert('Erreur modification statut');
    }
  };

  const handleRefuserCandidature = async () => {
    if (!window.confirm('Refuser cette candidature ? Cette action est irréversible.')) return;
    try {
      await http.patch(`/api/candidatures/${id}/statut`, { statut: 'refusee' });
      fetchData();
      alert('Candidature refusée');
    } catch (error) {
      alert('Erreur modification statut');
    }
  };

  const loadTestAnalyse = async () => {
    try {
      setLoadingAnalyse(true);
      const [sessionsResp] = await Promise.all([
        http.get(`/api/questionnaire-responses/candidature/${id}`)
      ]);
      const sessions = sessionsResp.data;
      if (sessions.length > 0 && sessions[0].id) {
        const analyseResp = await http.get(`/api/questionnaire-responses/analyse/${sessions[0].id}`);
        setTestAnalyse(analyseResp.data);
      } else {
        setTestAnalyse(null);
      }
    } catch (error) {
      console.error('Erreur chargement analyse test:', error);
      setTestAnalyse(null);
    } finally {
      setLoadingAnalyse(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'test') {
      loadTestAnalyse();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchQuestionnaires = async () => {
    try {
      const response = await http.get('/api/questionnaires', { params: { offre_id: candidature?.offre_id } });
      setTestQuestionnaires(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur questionnaires:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-500">Chargement…</div>
      </div>
    );
  }

  if (!candidature) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-500">Candidature non trouvée</div>
      </div>
    );
  }

  /* ───── Données d'affichage ───── */

  const initials = `${candidature.prenom?.[0] || ''}${candidature.nom?.[0] || ''}`.toUpperCase();
  const isTerminal = candidature.statut === 'refusee' || candidature.statut === 'retiree';
  const pipelineIdx = PIPELINE.findIndex((s) => s.id === candidature.statut);
  const currentIdx = isTerminal ? -1 : Math.max(pipelineIdx, 0);
  const triageProfil = buildTriage(candidature, null);
  const skillsProfil = splitSkills(candidature.competences);

  /* ════════════ Onglet Profil ════════════ */

  const renderProfil = () => (
    <div className="grid gap-8 p-6 lg:grid-cols-3 lg:p-8">
      <div className="space-y-8 lg:col-span-2">
        {candidature.lettre_motivation && (
          <section className="space-y-3">
            <Heading>Lettre de motivation</Heading>
            <div className="whitespace-pre-wrap rounded-xl bg-slate-50 p-5 text-sm leading-relaxed text-slate-700">
              {candidature.lettre_motivation}
            </div>
          </section>
        )}

        {/* Résultats du test */}
        {candidature.statut_test && candidature.statut_test !== 'en_cours' && (
          <section className="overflow-hidden rounded-xl border border-slate-200">
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <ClipboardList className="h-4 w-4 text-blue-600" />
                Résultats du test technique
              </h3>
              <button
                type="button"
                onClick={() => setActiveTab('test')}
                className="rounded text-sm font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
              >
                Voir l'analyse complète
              </button>
            </header>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-5 md:grid-cols-4">
              <Stat label="Statut" className={testStatut(candidature.statut_test).cls}>
                {testStatut(candidature.statut_test).label}
              </Stat>
              <Stat label="Score obtenu" className={cx('text-2xl tabular-nums', triageProfil.scoreOk ? 'text-emerald-600' : 'text-rose-600')}>
                {candidature.score_test !== null ? `${candidature.score_test}%` : '—'}
              </Stat>
              <Stat label="Bonnes réponses" className="tabular-nums">
                {candidature.nb_bonnes_reponses || 0} / {candidature.nb_total_questions || 0}
              </Stat>
              <Stat label="Date de fin" className="text-sm">
                {candidature.test_date_fin ? new Date(candidature.test_date_fin).toLocaleString('fr-FR') : '—'}
              </Stat>
            </dl>

            {/* Recommandation selon le score */}
            {candidature.score_test !== null && (
              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 px-5 py-4">
                <div className="flex items-start gap-3">
                  {triageProfil.scoreOk
                    ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                    : <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" />}
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Recommandation automatique</p>
                    <p className={cx('text-sm', triageProfil.scoreOk ? 'text-emerald-700' : 'text-rose-700')}>
                      {triageProfil.scoreOk ? 'Score suffisant : candidat recommandé pour examen' : 'Score insuffisant'}
                    </p>
                  </div>
                </div>
                {candidature.score_test >= 60 && candidature.statut === 'test' && (
                  <ActionButton tone="emerald" icon={UserCheck} onClick={handlePasserEnExamen}>
                    Passer en examen
                  </ActionButton>
                )}
                {candidature.score_test < 60 && candidature.statut === 'test' && (
                  <ActionButton tone="rose" icon={Trash2} onClick={handleRefuserCandidature}>
                    Refuser la candidature
                  </ActionButton>
                )}
              </div>
            )}
          </section>
        )}

        {/* Notes RH */}
        <section className="space-y-3">
          <Heading>Notes RH</Heading>
          <textarea
            className={controlCls}
            rows={3}
            value={candidature.notes_rh || ''}
            readOnly
            placeholder="Aucune note"
          />
        </section>
      </div>

      <aside className="space-y-8">
        {candidature.competences && (
          <section className="space-y-3">
            <Heading>Compétences</Heading>
            <div className="flex flex-wrap gap-2">
              {candidature.competences.split(',').map((comp, i) => (
                <span key={i} className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                  {comp.trim()}
                </span>
              ))}
            </div>
          </section>
        )}

        {candidature.cv_path && (
          <section className="space-y-3">
            <Heading>CV</Heading>
            <a
              href={`${API_URL}${candidature.cv_path}`}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center gap-3 rounded-xl border border-slate-200 p-4 transition hover:border-blue-300 hover:bg-blue-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FileText className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-900">Voir le CV</span>
                <span className="block text-xs text-slate-500">S'ouvre dans un nouvel onglet</span>
              </span>
              <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-blue-600" />
            </a>
          </section>
        )}
      </aside>
    </div>
  );

  /* ════════════ Onglet Test (triage) ════════════ */

  const renderTest = () => {
    if (loadingAnalyse) {
      return <div className="py-16 text-center text-sm text-slate-500">Chargement de l'analyse...</div>;
    }
    if (!testAnalyse) {
      return <div className="py-16 text-center text-sm text-slate-500">Aucun test effectué pour cette candidature.</div>;
    }

    const t = buildTriage(candidature, testAnalyse);
    const V = VERDICTS[t.verdict];
    const VerdictIcon = V.Icon;
    const session = testAnalyse.session;
    const delta = t.score !== null ? Math.round((t.score - t.seuil) * 10) / 10 : null;

    return (
      <div className="grid gap-8 p-6 lg:p-8 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-8">
          {/* Triage */}
          <section className="overflow-hidden rounded-2xl border border-slate-200">
            <div className={cx('flex flex-col gap-6 p-6 sm:flex-row sm:items-center lg:p-8', V.soft)}>
              <ScoreRing score={t.score} seuil={t.seuil} toneClass={V.ring} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-500">Triage semi-automatique</p>
                <h3 className={cx('mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight', V.text)}>
                  <VerdictIcon className="h-6 w-6 shrink-0" />
                  {V.title}
                </h3>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">{triageReason(t)}</p>

                {candidature.statut === 'test' ? (
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    {t.verdict === 'non_recommande' ? (
                      <>
                        <ActionButton tone="rose" icon={Trash2} onClick={handleRefuserCandidature}>Refuser la candidature</ActionButton>
                        <ActionButton tone="slate" variant="outline" icon={UserCheck} onClick={handlePasserEnExamen}>Passer en examen</ActionButton>
                      </>
                    ) : (
                      <>
                        <ActionButton
                          tone={t.verdict === 'recommande' ? 'emerald' : 'slate'}
                          variant={t.verdict === 'recommande' ? 'solid' : 'outline'}
                          icon={UserCheck}
                          onClick={handlePasserEnExamen}
                        >
                          Passer en examen
                        </ActionButton>
                        <ActionButton tone="rose" variant="outline" icon={Trash2} onClick={handleRefuserCandidature}>Refuser</ActionButton>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="mt-5 flex items-center gap-2 text-sm text-slate-500">
                    Statut actuel de la candidature : {statutBadge(candidature.statut)}
                  </p>
                )}
              </div>
            </div>

            {/* Critères */}
            <div className="divide-y divide-slate-200 px-6 lg:px-8">
              <CriterionRow
                state={t.scoreOk === null ? 'na' : t.scoreOk ? 'ok' : 'fail'}
                title="Test technique"
                requirement={`Seuil de réussite : ${t.seuil}%`}
                gauge={<BarGauge value={t.score} marker={t.seuil} ok={t.scoreOk} />}
                value={t.score === null ? '—' : `${t.score}%`}
                note={delta === null ? undefined : `${delta >= 0 ? '+' : '−'}${Math.abs(delta)} points`}
              />

              <CriterionRow
                state={t.expState === 'ok' ? 'ok' : t.expState === 'na' ? 'na' : 'warn'}
                title="Expérience"
                requirement={t.expMin !== null && t.expMax !== null ? `Fourchette demandée : ${t.expMin} à ${t.expMax} ans` : 'Fourchette non définie dans l’offre'}
                gauge={t.expState === 'na'
                  ? <p className="text-sm text-slate-400">Comparaison impossible</p>
                  : <RangeGauge min={t.expMin} max={t.expMax} value={t.expVal} state={t.expState} />}
                value={t.expVal === null ? '—' : `${t.expVal} an${t.expVal > 1 ? 's' : ''}`}
                note={t.expState === 'ok' ? 'Dans la fourchette' : t.expState === 'below' ? 'Sous le minimum' : t.expState === 'above' ? 'Au-dessus du maximum' : undefined}
              />

              <CriterionRow
                state={!t.hasProfil || t.skills.length === 0 ? 'na' : t.matched.length > 0 ? 'ok' : 'warn'}
                title="Compétences (indicatif)"
                requirement="Citées dans le profil recherché"
                gauge={t.hasProfil && t.skills.length > 0
                  ? <BarGauge tone="neutral" value={(t.matched.length / t.skills.length) * 100} />
                  : <p className="text-sm text-slate-400">{t.skills.length === 0 ? 'Aucune compétence renseignée' : 'Profil recherché non renseigné'}</p>}
                value={t.hasProfil && t.skills.length > 0 ? `${t.matched.length} / ${t.skills.length}` : '—'}
                note={t.hasProfil && t.skills.length > 0 ? 'retrouvées' : undefined}
              />
            </div>

            <p className="border-t border-slate-200 bg-slate-50 px-6 py-4 text-xs leading-relaxed text-slate-500 lg:px-8">
              Comment le triage est établi : « Recommandé » signifie test réussi et expérience dans la fourchette de l'offre.
              « À examiner » signifie test réussi mais expérience hors fourchette. « Non recommandé » signifie score sous le seuil.
              Les compétences sont indicatives et n'influencent pas le résultat. La décision finale vous revient.
            </p>
          </section>

          {/* Détail des réponses */}
          <section className="space-y-4">
            <Heading description={`${testAnalyse.questions_reponses.length} question${testAnalyse.questions_reponses.length > 1 ? 's' : ''} dans ce test.`}>
              Détail des réponses
            </Heading>
            <ol className="space-y-3">
              {testAnalyse.questions_reponses.map((qr, idx) => (
                <li
                  key={idx}
                  className={cx(
                    'rounded-xl border border-l-4 border-slate-200 bg-white p-5',
                    qr.est_correcte === true ? 'border-l-emerald-500' : qr.est_correcte === false ? 'border-l-rose-500' : 'border-l-slate-300',
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900">Question {idx + 1}: {qr.question}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        Type: <span className="uppercase">{qr.type}</span>
                      </p>
                    </div>
                    {qr.est_correcte !== null && (
                      <span className={cx(
                        'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
                        qr.est_correcte ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-rose-50 text-rose-700 ring-rose-600/20',
                      )}>
                        {qr.est_correcte ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        {qr.est_correcte ? 'Correct' : 'Incorrect'}
                      </span>
                    )}
                  </div>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <p className="mb-1 text-xs text-slate-500">Réponse du candidat</p>
                      <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-900">
                        {Array.isArray(qr.reponse_candidat) ? qr.reponse_candidat.join(', ') : qr.reponse_candidat || 'Non répondu'}
                      </div>
                    </div>
                    {qr.reponse_attendue && (
                      <div>
                        <p className="mb-1 text-xs text-slate-500">Réponse attendue</p>
                        <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-900">
                          {qr.reponse_attendue}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* Colonne latérale */}
        <aside className="min-w-0 space-y-8">
          <section className="space-y-4">
            <Heading>Session de test</Heading>
            <dl className="divide-y divide-slate-100 rounded-xl border border-slate-200 px-5">
              <div className="flex items-baseline justify-between gap-4 py-3">
                <dt className="text-sm text-slate-500">Score obtenu</dt>
                <dd className="text-right">
                  <span className={cx('text-lg font-semibold tabular-nums', t.scoreOk === false ? 'text-rose-600' : 'text-emerald-600')}>
                    {session.score_obtenu !== null ? `${session.score_obtenu}%` : '—'}
                  </span>
                  <span className="block text-xs text-slate-400">Seuil: {session.note_passage}%</span>
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 py-3">
                <dt className="text-sm text-slate-500">Bonnes réponses</dt>
                <dd className="font-semibold tabular-nums text-slate-900">{session.nb_bonnes_reponses} / {session.nb_total_questions}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 py-3">
                <dt className="text-sm text-slate-500">Durée</dt>
                <dd className="font-semibold tabular-nums text-slate-900">{session.duree_minutes} min</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 py-3">
                <dt className="text-sm text-slate-500">Statut</dt>
                <dd className={cx('font-semibold', testStatut(session.statut_test).cls)}>{testStatut(session.statut_test).label}</dd>
              </div>
            </dl>
          </section>

          <section className="space-y-3">
            <Heading>Compétences du candidat</Heading>
            <div className="flex flex-wrap gap-2">
              {testAnalyse.candidat.competences ? (
                testAnalyse.candidat.competences.split(',').map((comp, i) => {
                  const found = t.matched.includes(comp.trim());
                  return (
                    <span
                      key={i}
                      className={cx(
                        'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset',
                        found ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-blue-50 text-blue-700 ring-blue-600/20',
                      )}
                      title={found ? 'Citée dans le profil recherché' : undefined}
                    >
                      {found && <Check className="h-3 w-3" />}
                      {comp.trim()}
                    </span>
                  );
                })
              ) : (
                <span className="text-sm text-slate-500">Aucune compétence renseignée</span>
              )}
            </div>
            {testAnalyse.offre.profil_recherche && (
              <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                <p className="mb-1 font-medium text-slate-900">Profil recherché:</p>
                <p className="whitespace-pre-wrap leading-relaxed text-slate-700">{testAnalyse.offre.profil_recherche}</p>
              </div>
            )}
          </section>
        </aside>
      </div>
    );
  };

  /* ════════════ Onglet Entretiens ════════════ */

  const renderEntretiens = () => (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Entretiens</h3>
          <p className="text-sm text-slate-500">Planifiez et suivez les entretiens de ce candidat.</p>
        </div>
        <button type="button" onClick={() => openEntretienModal()} className={btnPrimary}>
          <Plus className="h-4 w-4" />
          Planifier
        </button>
      </div>

      {entretiens.length === 0 ? (
        <EmptyState>Aucun entretien planifié</EmptyState>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {entretiens.map((entretien) => {
            const date = new Date(entretien.date_heure_entretien);
            const upcoming = date > new Date();
            return (
              <li key={entretien.id} className="flex gap-4 rounded-xl border border-slate-200 p-5">
                <div className={cx(
                  'flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl',
                  upcoming ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500',
                )}>
                  <span className="text-xl font-semibold leading-none">{date.getDate()}</span>
                  <span className="mt-0.5 text-xs">{date.toLocaleDateString('fr-FR', { month: 'short' })}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{date.toLocaleString('fr-FR')}</p>
                      <p className="text-sm text-slate-500">{entretien.type_entretien}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {new Date(entretien.date_heure_entretien) > new Date() && (
                        <button
                          type="button"
                          onClick={() => openEntretienModal(entretien)}
                          className="rounded-md p-1.5 text-blue-600 transition hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
                          title="Modifier"
                          aria-label="Modifier l'entretien"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleSupprimerEntretien(entretien.id)}
                        className="rounded-md p-1.5 text-rose-600 transition hover:bg-rose-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-600/40"
                        title="Supprimer"
                        aria-label="Supprimer l'entretien"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5 text-sm text-slate-700">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="min-w-0 break-words">{entretien.lieu_entretien}</span>
                    </div>
                    {entretien.evaluateurs_ids && entretien.evaluateurs_ids.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 shrink-0 text-slate-400" />
                        {entretien.evaluateurs_ids.length} évaluateur(s)
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  /* ════════════ Onglet Historique ════════════ */

  const renderHistorique = () => (
    <div className="grid gap-8 p-6 lg:p-8 xl:grid-cols-[360px_minmax(0,1fr)]">
      {/* Formulaire d'ajout de commentaire */}
      <section className="space-y-3 xl:sticky xl:top-6 xl:self-start">
        <Heading description="Visible dans l'historique de la candidature.">Ajouter un commentaire</Heading>
        <textarea
          className={controlCls}
          rows={4}
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          placeholder="Écrivez votre commentaire..."
          aria-label="Commentaire"
        />
        <div className="flex justify-end">
          <button type="button" onClick={handleAddCommentaire} className={btnPrimary}>
            <Send className="h-4 w-4" />
            Envoyer
          </button>
        </div>
      </section>

      {/* Timeline de l'historique */}
      <section className="min-w-0 space-y-4">
        <Heading>Historique & Commentaires</Heading>
        {historique.length === 0 ? (
          <EmptyState>Aucun historique enregistré</EmptyState>
        ) : (
          <ol className="relative ml-4 space-y-7 border-l border-slate-200 pl-8">
            {historique.map((entry) => (
              <li key={entry.id} className="relative">
                <span className="absolute -left-12 flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-blue-600 ring-4 ring-white">
                  <MessageSquare className="h-4 w-4" />
                </span>
                <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                  <span className="font-medium text-slate-900">{entry.statut}</span>
                  <span className="text-xs text-slate-500">
                    {entry.date_action ? new Date(entry.date_action).toLocaleString('fr-FR') : '—'}
                  </span>
                </div>
                {entry.commentaire && (
                  <p className="mt-1.5 rounded-lg bg-slate-50 px-4 py-3 text-sm italic text-slate-600">"{entry.commentaire}"</p>
                )}
                <p className="mt-1.5 text-xs text-slate-400">
                  Par {entry.acteur_nom || entry.acteur_prenom || 'RH'}
                </p>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );

  /* ════════════ Rendu ════════════ */

  return (
    <div className="min-h-screen bg-slate-50">
      <BlueHeader
        title="Détail Candidature"
        subtitle={`${candidature.prenom} ${candidature.nom}`}
        icon={User}
        actions={[
          { label: 'Retour', icon: ArrowLeft, onClick: () => navigate('/candidatures'), primary: false }
        ]}
      />

      <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {/* Fiche candidat */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-start lg:justify-between lg:p-8">
            <div className="flex min-w-0 gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-2xl font-semibold text-white">
                {initials || <User className="h-8 w-8" />}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{candidature.prenom} {candidature.nom}</h2>
                  {statutBadge(candidature.statut)}
                </div>
                <p className="mt-1 text-slate-600">
                  Candidature pour <span className="font-medium text-slate-900">{candidature.offre_titre}</span>
                </p>
                <dl className="mt-5 flex flex-wrap gap-x-10 gap-y-4 text-sm">
                  <div>
                    <dt className="text-xs text-slate-500">Email</dt>
                    <dd className="mt-0.5 flex items-center gap-2 text-slate-800"><Mail className="h-4 w-4 text-slate-400" />{candidature.email}</dd>
                  </div>
                  {candidature.telephone && (
                    <div>
                      <dt className="text-xs text-slate-500">Téléphone</dt>
                      <dd className="mt-0.5 flex items-center gap-2 text-slate-800"><Phone className="h-4 w-4 text-slate-400" />{candidature.telephone}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-xs text-slate-500">Date de candidature</dt>
                    <dd className="mt-0.5 flex items-center gap-2 text-slate-800">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      {candidature.created_at ? new Date(candidature.created_at).toLocaleDateString('fr-FR') : '—'}
                    </dd>
                  </div>
                  {candidature.source && (
                    <div>
                      <dt className="text-xs text-slate-500">Source</dt>
                      <dd className="mt-0.5 capitalize text-slate-800">{candidature.source}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>

            {/* Actions selon le statut */}
            <div className="flex flex-wrap gap-2 lg:max-w-sm lg:justify-end">
              {candidature.statut === 'recue' && testQuestionnaires.length > 0 && (
                <ActionButton tone="blue" icon={ClipboardList} onClick={() => setShowTestModal(true)}>
                  Planifier test
                </ActionButton>
              )}
              {candidature.statut === 'test' && (
                <ActionButton tone="amber" variant="outline" icon={RefreshCw} onClick={handleResetTest}>
                  Réinitialiser test
                </ActionButton>
              )}
              {candidature.statut !== 'embauchee' && (
                <ActionButton tone="emerald" icon={UserCheck} onClick={() => setShowOnboardingModal(true)}>
                  Transformer en employé
                </ActionButton>
              )}
            </div>
          </div>

          <div className="border-t border-slate-200 bg-slate-50/70 px-6 py-5 lg:px-8">
            <Pipeline current={currentIdx} />
            {isTerminal && (
              <p className="mt-4 text-center text-sm text-slate-500">
                Processus interrompu : la candidature est au statut « {STATUT_LABELS[candidature.statut]} ».
              </p>
            )}
          </div>
        </section>

        {/* Onglets */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <nav className="flex gap-1 overflow-x-auto border-b border-slate-200 px-4 sm:px-6" aria-label="Sections de la candidature">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const count = tab.id === 'entretiens' ? entretiens.length : tab.id === 'historique' ? historique.length : 0;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cx(
                    'flex items-center gap-2.5 whitespace-nowrap border-b-2 px-3 py-3.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40',
                    isActive ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-900',
                  )}
                >
                  <Icon className={cx('h-4 w-4 shrink-0', isActive ? 'text-blue-600' : 'text-slate-400')} />
                  {tab.label}
                  {count > 0 && (
                    <span className={cx('rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums', isActive ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600')}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {activeTab === 'profil' && renderProfil()}
          {activeTab === 'test' && renderTest()}
          {activeTab === 'entretiens' && renderEntretiens()}
          {activeTab === 'historique' && renderHistorique()}
        </div>
      </div>

      {/* Modal Entretien */}
      {showEntretienModal && (
        <Modal
          title={`${selectedEntretien ? 'Modifier' : 'Planifier'} un entretien`}
          onClose={() => setShowEntretienModal(false)}
          footer={(
            <>
              <button type="button" onClick={() => setShowEntretienModal(false)} className={btnSecondary}>
                Annuler
              </button>
              <button
                type="button"
                onClick={() => selectedEntretien ? handleModifierEntretien(selectedEntretien) : handlePlanifierEntretien()}
                className={btnPrimary}
              >
                {selectedEntretien ? 'Modifier' : 'Planifier'}
              </button>
            </>
          )}
        >
          <TextField
            label="Date & Heure"
            type="datetime-local"
            required
            value={entretienForm.date_heure_entretien}
            onChange={(e) => setEntretienForm({ ...entretienForm, date_heure_entretien: e.target.value })}
          />
          <TextField
            label="Lieu / Lien"
            required
            value={entretienForm.lieu_entretien}
            onChange={(e) => setEntretienForm({ ...entretienForm, lieu_entretien: e.target.value })}
            placeholder="Salle de réunion ou lien visio"
          />
          <SelectField
            label="Type d'entretien"
            value={entretienForm.type_entretien}
            onChange={(e) => setEntretienForm({ ...entretienForm, type_entretien: e.target.value })}
          >
            <option value="Physique">Physique</option>
            <option value="Visio">Visioconférence</option>
            <option value="Téléphone">Téléphone</option>
          </SelectField>

          <fieldset>
            <legend className="mb-1.5 flex w-full items-center justify-between text-sm font-medium text-slate-700">
              Évaluateurs
              <span className="text-xs font-normal text-slate-500">{entretienForm.evaluateurs_ids.length} sélectionné(s)</span>
            </legend>
            <div className="mb-3">
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={evaluateurSearch}
                onChange={(e) => setEvaluateurSearch(e.target.value)}
                className={controlCls}
              />
            </div>
            {evaluateurs.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center text-sm text-slate-500">Aucun utilisateur disponible</p>
            ) : (
              <ul className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
                {evaluateurs
                  .filter((evaluateur) => {
                    const search = evaluateurSearch.toLowerCase();
                    return (
                      evaluateur.nom?.toLowerCase().includes(search) ||
                      evaluateur.prenom?.toLowerCase().includes(search) ||
                      evaluateur.username?.toLowerCase().includes(search) ||
                      evaluateur.profil?.toLowerCase().includes(search)
                    );
                  })
                  .map((evaluateur) => {
                    const checked = entretienForm.evaluateurs_ids.map(Number).includes(Number(evaluateur.id));
                    return (
                      <li key={evaluateur.id}>
                        <label className={cx('flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm transition', checked ? 'bg-blue-50/60' : 'hover:bg-slate-50')}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const current = entretienForm.evaluateurs_ids.map(Number);
                              const selected = checked
                                ? current.filter((x) => x !== Number(evaluateur.id))
                                : [...current, parseInt(evaluateur.id)];
                              setEntretienForm({ ...entretienForm, evaluateurs_ids: selected });
                            }}
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                          />
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
                            {`${evaluateur.nom?.[0] || ''}${evaluateur.prenom?.[0] || ''}`.toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <span className="truncate text-slate-800">{evaluateur.nom} {evaluateur.prenom}</span>
                            {evaluateur.profil && (
                              <span className="ml-2 text-xs text-slate-500">({evaluateur.profil})</span>
                            )}
                          </div>
                        </label>
                      </li>
                    );
                  })}
              </ul>
            )}
          </fieldset>
        </Modal>
      )}

      {/* Modal Test */}
      {showTestModal && (
        <Modal
          title="Planifier un test"
          onClose={() => setShowTestModal(false)}
          footer={(
            <>
              <button type="button" onClick={() => setShowTestModal(false)} className={btnSecondary}>
                Annuler
              </button>
              <button type="button" onClick={handlePlanifierTest} className={btnPrimary}>
                Planifier
              </button>
            </>
          )}
        >
          {testQuestionnaires.length > 1 && (
            <SelectField
              label="Questionnaire"
              required
              value={selectedQuestionnaire}
              onChange={(e) => setSelectedQuestionnaire(e.target.value)}
            >
              <option value="">— Choisir un questionnaire —</option>
              {testQuestionnaires.map((q) => (
                <option key={q.id} value={q.id}>{q.titre} ({q.duree_minutes} min)</option>
              ))}
            </SelectField>
          )}
          {testQuestionnaires.length === 1 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
              <p className="text-sm text-blue-900">
                <strong>Questionnaire :</strong> {testQuestionnaires[0].titre} ({testQuestionnaires[0].duree_minutes} min)
              </p>
            </div>
          )}
          <TextField
            label="Date limite (optionnel)"
            type="datetime-local"
            value={testDateLimite}
            onChange={(e) => setTestDateLimite(e.target.value)}
          />
        </Modal>
      )}

      {/* Modal Onboarding */}
      {showOnboardingModal && (
        <Modal
          title="Transformer en employé"
          onClose={() => setShowOnboardingModal(false)}
          footer={(
            <>
              <button type="button" onClick={() => setShowOnboardingModal(false)} className={btnSecondary}>
                Annuler
              </button>
              <button
                type="button"
                onClick={handleOnboarding}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40 focus-visible:ring-offset-2"
              >
                Transformer
              </button>
            </>
          )}
        >
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
              <UserCheck className="h-4 w-4" />
              Onboarding automatique
            </h4>
            <p className="mt-1 text-xs text-emerald-800">
              Un employé sera automatiquement créé avec un contrat en brouillon.
            </p>
          </div>
          <TextField
            label="Date d'embauche"
            type="date"
            required
            value={onboardingData.date_embauche}
            onChange={(e) => setOnboardingData({ ...onboardingData, date_embauche: e.target.value })}
          />
          <SelectField
            label="Type de contrat"
            required
            value={onboardingData.type_contrat}
            onChange={(e) => setOnboardingData({ ...onboardingData, type_contrat: e.target.value })}
          >
            <option value="CDI">CDI</option>
            <option value="CDD">CDD</option>
            <option value="Stage">Stage</option>
            <option value="Apprentissage">Apprentissage</option>
          </SelectField>
          <TextArea
            label="Commentaire (optionnel)"
            rows={2}
            value={onboardingData.commentaire}
            onChange={(e) => setOnboardingData({ ...onboardingData, commentaire: e.target.value })}
            placeholder="Informations complémentaires sur l'embauche..."
          />
        </Modal>
      )}
    </div>
  );
};

export default CandidatureDetail;